import { Injectable } from '@angular/core';

import {
  asyncScheduler,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  ignoreElements,
  map,
  merge,
  Observable,
  of,
  pairwise,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
  throttleTime
} from 'rxjs';
import moment from 'moment';

import {
  DEFAULT_AUTO_REMOVE_HOURS,
  getSizeGroup,
  getWaitingTemplate,
  normalizePartySize,
  Party,
  PartySource,
  PartyTypes,
  WaitingParty,
} from '@shared/party';
import { WebSocketEventsEnum } from '@shared/websocket';
import { MS_PER_DAY, MS_PER_HOUR } from '@shared/core';
import { IPageData, IParty } from '@tablesready/common-ui';
import { TokenService } from '@tablesready/frontend-auth';
import {
  AuthRestaurantService,
  EventSequenceService,
  PartiesQuery,
  PartiesStore,
  SocketService
} from '@tablesready/frontend-store';
import { PartiesService } from '@tablesready/frontend-api-services';
import {
  getReservationWaitlistHoursAhead,
  isInReservationWaitlistWindow,
} from '@tablesready/frontend-table';

import { ServicesService } from './services.service';
import { ResourcesService } from './resources.service';

/**
 * TODO:
 * - consider keeping all available types of parties - walk in, reservation and to go
 * - load all available parties
 * - add methods to get counts for each type
 */
// Coalesces a burst of refetch triggers into one leading and one trailing request.
const REFETCH_THROTTLE_MS = 2000;

@Injectable({providedIn: 'root'})
export class ActivePartiesService {
  private _includeSeated = false;
  private autoRemove = 0;
  private daysAhead = 0;
  private reservationWaitlistHoursAhead = 0;
  private services: {_id: string; name: string;}[] = [];
  private resources: {_id: string; name: string;}[] = [];
  private serviceIdMap: Record<string, number> = {};
  private resourceIdMap: Record<string, number> = {};
  private hideNoPhone = false;
  private showWaiting = false;
  // Fires for party websocket events whose subject never lives in the Akita store (completed/
  // canceled parties, deletions of them); the day grid refetches its completed parties on it.
  private readonly nonStorePartyChange$ = new Subject<void>();

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly partiesService: PartiesService,
    private readonly query: PartiesQuery,
    private readonly store: PartiesStore,
    private readonly socketService: SocketService,
    private readonly eventSequenceService: EventSequenceService,
    private readonly tokenService: TokenService,
  ) {}

  private readonly locationConfig = this.authRestaurantService.selectWithAccountAndLocation()
    .pipe(
      map(({ location }) => ({
        locationId: location._id,
        autoRemove: location.autoRemove || DEFAULT_AUTO_REMOVE_HOURS,
        daysAhead: location.displayOptions.future ? 30 : 0.5,
        reservationWaitlistHoursAhead: getReservationWaitlistHoursAhead(location),
        hideNoPhone: !!(location.integrations?.square && location.integrations.squareOptions?.hideNoPhone),
        showWaiting: !!(location.displayOptions?.footer || location.waitFields.estimated_waits),
        timezone: location.timezone,
      })),
      startWith({
        locationId: null,
        autoRemove: DEFAULT_AUTO_REMOVE_HOURS,
        daysAhead: 0,
        reservationWaitlistHoursAhead: 0,
        hideNoPhone: false,
        showWaiting: false,
        timezone: '',
      }),
      distinctUntilChanged((a, b) =>
        a.locationId === b.locationId &&
        a.autoRemove === b.autoRemove &&
        a.daysAhead === b.daysAhead &&
        a.reservationWaitlistHoursAhead === b.reservationWaitlistHoursAhead &&
        a.hideNoPhone === b.hideNoPhone &&
        a.showWaiting === b.showWaiting &&
        a.timezone === b.timezone,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
  );

  setIncludeSeated(value: boolean): void {
    this._includeSeated = value;
  }

  load(options?: { includeSeated?: boolean }): Observable<Party[]> {
    // Without a location the API answers 403, which getActiveParties turns into an empty list
    if (this.tokenService.hasNoLocationToken()) {
      return of<Party[]>([]);
    }

    const requestedWithSeated = this._includeSeated || options?.includeSeated;

    return this.partiesService.getActiveParties({
      ...options,
      includeSeated: requestedWithSeated,
    }).pipe(
      tap(({ parties }) => {
        // Guard: if we now require seated but this request was made without them, discard
        if (this._includeSeated && !requestedWithSeated) {
          return;
        }
        this.store.set(parties);
      }),
      map(({ parties }) => parties),
    );
  }

  selectWaitlist(filters: Observable<IPageData>): Observable<{parties: Party[]; count: number; waitingPartiesData?: {waitingParties: WaitingParty[]; guestCount: number;};}> {
    return combineLatest([
      this.getWaitlistParties(),
      filters.pipe(
        map(data => {
          const filters: Filters = {
            search: data.search,
            sort: data.sortBy,
            sortReverse: data.sortReverse ? -1 : 1,
            sizeGroup: undefined,
          };
          if (data.sizeGroup) {
            if (data.sizeGroup === '9+') {
              filters.sizeGroup = [9, Infinity];
            } else {
              const [min, max] = data.sizeGroup.split('-').map(Number);
              filters.sizeGroup = [min, max];
            }
          }

          return filters;
        }),
        startWith({
          search: undefined,
          sort: undefined,
          sortReverse: undefined,
          sizeGroup: undefined,
        }),
        pairwise(),
        filter(([prev, curr]) => {
          return prev.sort !== curr.sort ||
            prev.sortReverse !== curr.sortReverse ||
            (prev.sizeGroup || '').toString() !== (curr.sizeGroup || '').toString();
        }),
        map(([,curr]) => curr),
      ),
      this.authRestaurantService.selectWithAccountAndLocation()
        .pipe(
          map(({location}) => {
            return {
              autoRemove: location.autoRemove || DEFAULT_AUTO_REMOVE_HOURS,
              daysAhead: location.displayOptions.future ? 30 : 0.5,
              reservationWaitlistHoursAhead: getReservationWaitlistHoursAhead(location),
              hideNoPhone: location.integrations?.square && location.integrations.squareOptions?.hideNoPhone,
              showWaiting: location.displayOptions?.footer || location.waitFields.estimated_waits,
            }
          }),
          startWith({
            autoRemove: DEFAULT_AUTO_REMOVE_HOURS,
            daysAhead: 0,
            reservationWaitlistHoursAhead: 0,
            hideNoPhone: false,
            showWaiting: false,
          }),
          pairwise(),
          filter(([prev, curr]) => {
            return prev.autoRemove !== curr.autoRemove
              || prev.daysAhead !== curr.daysAhead
              || prev.reservationWaitlistHoursAhead !== curr.reservationWaitlistHoursAhead
              || prev.hideNoPhone !== curr.hideNoPhone
              || prev.showWaiting !== curr.showWaiting
          }),
          tap(([, data]) => {
            this.autoRemove = data.autoRemove;
            this.daysAhead = data.daysAhead;
            this.reservationWaitlistHoursAhead = data.reservationWaitlistHoursAhead;
            this.hideNoPhone = data.hideNoPhone;
            this.showWaiting = data.showWaiting;
          }),
        ),
      this.servicesService.selectAllSortedByName().pipe(tap(services => {
        this.services = services;
        for (let i = 0; i < services.length; i++) {
          this.serviceIdMap[services[i]._id] = i;
        }
      })),
      this.resourcesService.selectAllSortedByName().pipe(tap(resources => {
        this.resources = resources;
        for (let i = 0; i < resources.length; i++) {
          this.resourceIdMap[resources[i]._id] = i;
        }
      })),
    ])
      .pipe(
        map(([parties, {search, sizeGroup, sort, sortReverse}]) => {
          const now = Date.now();
          const start = now - this.autoRemove * MS_PER_HOUR;
          const end = now + this.daysAhead * MS_PER_DAY;
          let regexp: RegExp | null = null;
          if (search && search.length > 2) {
            const _search = search.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
            regexp = new RegExp(_search, 'i');
          }
          const serviceIds: string[] = [];
          const resourceIds: string[] = [];
          if (regexp) {
            for (let i = 0; i < this.services.length; i++) {
              const service = this.services[i];
              if (service.name.search(regexp) > -1) {
                serviceIds.push(service._id);
              }
            }
            for (let i = 0; i < this.resources.length; i++) {
              const resource = this.resources[i];
              if (resource.name.search(regexp) > -1) {
                resourceIds.push(resource._id);
              }
            }
          }

          const filteredParties: Party[] = [];
          const waitingParties: Party[] = [];
          for (let i = 0; i < parties.length; i++) {
            const party = parties[i];
            // Exclude by time range
            if (party.quoted_time <= start) {
              continue;
            }

            const isOutOfRange = party.type === PartyTypes.reservation
              ? !isInReservationWaitlistWindow(
                party.quoted_time,
                this.reservationWaitlistHoursAhead,
                now,
              )
              : party.quoted_time >= end;
            if (isOutOfRange) {
              continue;
            }

            // Exclude square parties without phone number
            if (this.hideNoPhone && party.source === PartySource.square && party.phone === '') {
              continue;
            }

            waitingParties.push(party);

            // same headcount the footer badges count with, so filter and badge agree
            const headcount = normalizePartySize(party.size);
            if (sizeGroup && (headcount < sizeGroup[0] || headcount > sizeGroup[1])) {
              continue;
            }

            if (regexp && !(
              party.name.search(regexp) > -1
              || String(party.note).search(regexp) > -1
              || serviceIds.length > 0 && party.services.some(id => serviceIds.includes(id))
              || resourceIds.length > 0 && party.resources.some(id => resourceIds.includes(id))
            )) {
              continue;
            }

            filteredParties.push(party);
          }

          this.sort(filteredParties, sort, sortReverse as SortDirection);

          return {
            // Return page * limit parties instead of limit
            parties: filteredParties,
            count: filteredParties.length,
            waitingPartiesData: this.getWaitingParties(waitingParties),
          };
        }),
      );
  }

  selectReservations(
    filters: Observable<IPageData>,
  ): Observable<{ parties: Party[]; count: number }> {
    return combineLatest([
      this.getReservationParties(),
      filters.pipe(
        map(data => {
          return {
            search: data.search?.trim() ?? '',
            sort: data.sortBy ?? 'quoted_time',
            sortReverse: (data.sortReverse ? -1 : 1) as SortDirection,
            date: data.date ?? null,
          };
        }),
      ),
      this.servicesService.selectAllSortedByName(),
      this.resourcesService.selectAllSortedByName(),
      this.locationConfig,
    ]).pipe(
      map(([parties, { search, sort, sortReverse, date }, services, resources, { timezone } ]) => {
        const regexp =
          search && search.length > 2
            ? new RegExp(search.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'), 'i')
            : null;

        const filterDateString = date
          ? moment.tz(date, timezone).format('YYYY-MM-DD')
          : null;
        const matchedServiceIds = regexp
          ? services.filter(s => regexp.test(s.name)).map(s => s._id)
          : [];
        const matchedResourceIds = regexp
          ? resources.filter(r => regexp.test(r.name)).map(r => r._id)
          : [];

        const filteredParties = parties.filter(party => {
          if (filterDateString) {
            const partyDateString = moment.tz(party.quoted_time, timezone).format('YYYY-MM-DD');
            if (partyDateString !== filterDateString) {
              return false;
            }
          }

          if (!regexp) {
            return true;
          }

          const matchesText =
            regexp.test(party.name ?? '') ||
            regexp.test(String(party.note ?? ''));
          const matchesService =
            matchedServiceIds.length > 0 &&
            party.services?.some(id => matchedServiceIds.includes(id));
          const matchesResource =
            matchedResourceIds.length > 0 &&
            party.resources?.some(id => matchedResourceIds.includes(id));

          return matchesText || matchesService || matchesResource;
        });

        const sortedParties = [...filteredParties];
        this.sort(sortedParties, sort, sortReverse);

        return {
          // Return all filtered parties instead of slicing
          parties: sortedParties,
          count: filteredParties.length,
        };
      }),
    );
  }


  loadReservationsForDate(date: number, search: string = ''): Observable<{ parties: Party[]; count: number }> {
    return this.partiesService.getReservations({
      date: String(date),
      limit: 1000,
      page: 1,
      sortBy: 'quoted_time',
      sortReverse: false,
      search
    }).pipe(
      tap(({ parties }) => this.upsertParties(parties)),
    );
  }

  /**
   * Idempotently insert or update a party in the store. Safe to call from both
   * HTTP responses (on create/update) and websocket events — it checks whether
   * the party is already present and avoids duplication.
   *
   * No-op for parties that should not be in the store (togo / canceled / completed).
   */
  upsertParty(party: Party): void {
    if (!this.shouldBeInStore(party)) {
      return;
    }
    if (this.query.hasEntity(party._id)) {
      this.store.update(party._id, party);
    } else {
      this.store.add(party);
    }
  }

  /**
   * Bulk variant of {@link upsertParty}: applies the whole collection in a
   * single store emission via `upsertMany` (idKey-based add/update). Parties
   * that should not be in the store (togo / canceled / completed) are filtered
   * out first.
   */
  upsertParties(parties: Party[]): void {
    const toStore = parties.filter((party) => this.shouldBeInStore(party));
    if (toStore.length > 0) {
      this.store.upsertMany(toStore);
    }
  }

  loadAllReservations(search: string = ''): Observable<{ parties: Party[]; count: number }> {
    return this.partiesService.getReservations({
      date: '',
      limit: 1000,
      page: 1,
      sortBy: 'quoted_time',
      sortReverse: false,
      search,
    }).pipe(
      tap(({ parties }) => {
        for (let i = 0; i < parties.length; i++) {
          const party = parties[i];
          if (this.query.hasEntity(party._id)) {
            this.store.update(party._id, party);
          } else {
            this.store.add(party);
          }
        }
      }),
    );
  }

  getWaitlistParties(): Observable<Party[]> {
    return combineLatest([
      this.query.selectAll(),
      this.locationConfig
    ]).pipe(
      map(([parties, config]) =>
        parties.filter(party => this.shouldBeInWaitlist(party, config))
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  waitlistCount(): Observable<number> {
    return this.getWaitlistParties()
      .pipe(
        map(parties => parties.length)
      );
  }

  getReservationParties(): Observable<Party[]> {
    return combineLatest([
      this.query.selectAll(),
      this.locationConfig
    ]).pipe(
      map(([parties, config]) =>
        parties.filter(party => this.shouldBeInReservations(party, config))
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  reservationsCount(): Observable<number> {
    return combineLatest([
      this.getReservationParties(),
      this.locationConfig,
    ]).pipe(
      map(([parties, config]) => {
        const endOfToday = config.timezone
          ? moment.tz(config.timezone).endOf('day').valueOf()
          : moment().endOf('day').valueOf();
        return parties.filter(p => p.quoted_time <= endOfToday).length;
      }),
    );
  }

  getFloorPlanParties(): Observable<Party[]> {
    return this.query.selectAll().pipe(
      map(parties => parties.filter(party =>
        party.type !== PartyTypes.togo
        && party.canceled_time === null
        && party.completedAt === 0
      )),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  loadPartiesForDate(
    startOfDay: number,
    endOfDay: number,
    includeCompleted = false,
  ): Observable<Party[]> {
    return this.partiesService.getPartiesByWorkTime({
      workTime: [[startOfDay, endOfDay]],
      served: null,
      type: null,
      includeCompleted,
    }).pipe(
      map((parties) => parties),
    );
  }

  getPartiesForDate(
    selectedDate$: Observable<number>,
    timezone: string,
    options?: { includeCompleted?: boolean },
  ): Observable<Party[]> {
    if (options?.includeCompleted) {
      return this.getPartiesForDateWithCompleted(selectedDate$, timezone);
    }

    return combineLatest([
      selectedDate$,
      this.getFloorPlanParties(),
    ]).pipe(
      switchMap(([selectedDate, activeParties]) => {
        const { startOfDay, endOfDay } = this.getDayBounds(selectedDate, timezone);
        const isToday = moment.tz(selectedDate, timezone).isSame(moment.tz(timezone), 'day');
        const inWindow = (party: Party): boolean =>
          party.quoted_time >= startOfDay && party.quoted_time < endOfDay;

        if (isToday) {
          return of(activeParties);
        }

        return this.loadPartiesForDate(startOfDay, endOfDay).pipe(
          map(historicParties => {
            const partiesMap = new Map<string, Party>();

            historicParties.forEach(party => {
              partiesMap.set(party._id, party);
            });

            activeParties.forEach(party => {
              if (inWindow(party)) {
                partiesMap.set(party._id, party);
              }
            });

            return Array.from(partiesMap.values());
          }),
          // The error must not escape this switchMap: it would complete the
          // outer stream and permanently freeze every consumer's view. Fall
          // back to the in-window live parties; the next store emission or
          // date change retries the request.
          catchError((error) => {
            console.error('getPartiesForDate: failed to load parties for date', error);

            return of(activeParties.filter(inWindow));
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Day-grid variant: parties quoted, seated or completed within the day. Completed parties never
   * enter the store, so they come from the server even for today — but only need refetching when
   * a party can have left the store (completed, canceled, deleted) or a completed party changed.
   * Every other store emission (a text sent, a note edited) is overlaid on the last response.
   */
  private getPartiesForDateWithCompleted(selectedDate$: Observable<number>, timezone: string): Observable<Party[]> {
    const storeParties$ = this.getFloorPlanParties().pipe(shareReplay({ bufferSize: 1, refCount: true }));
    const storeMembership$ = storeParties$.pipe(
      map(parties => parties
        .map(party => party._id)
        .sort((a, b) => a.localeCompare(b))
        .join()),
      distinctUntilChanged(),
    );
    const refetch$ = merge(storeMembership$, this.nonStorePartyChange$).pipe(
      throttleTime(REFETCH_THROTTLE_MS, asyncScheduler, { leading: true, trailing: true }),
    );

    return selectedDate$.pipe(
      switchMap((selectedDate) => {
        const { startOfDay, endOfDay } = this.getDayBounds(selectedDate, timezone);
        // Same match rule as the server's includeCompleted query.
        const inDay = (time: number | null | undefined): boolean =>
          time != null && time >= startOfDay && time < endOfDay;
        const inWindow = (party: Party): boolean =>
          inDay(party.quoted_time) || inDay(party.seated_time) || inDay(party.completedAt);
        const serverParties$ = refetch$.pipe(
          switchMap(() => this.loadPartiesForDate(startOfDay, endOfDay, true).pipe(
            // Must not escape: that would complete the stream and freeze the grid. The store
            // overlay below still shows the day's active parties; the next trigger retries.
            catchError((error) => {
              console.error('getPartiesForDate: failed to load parties for date', error);

              return of<Party[]>([]);
            }),
          )),
        );

        return combineLatest([serverParties$, storeParties$]).pipe(
          // The store copy is fresher for every party it holds, including one moved off this day.
          map(([serverParties, activeParties]) => {
            const storeIds = new Set(activeParties.map(party => party._id));

            return [
              ...serverParties.filter(party => !storeIds.has(party._id)),
              ...activeParties.filter(inWindow),
            ];
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  // Half-open [startOfDay, nextDayStart): endOf('day') is 23:59:59.999 and the backend compares
  // `< end`, which would drop the day's final millisecond.
  private getDayBounds(selectedDate: number, timezone: string): { endOfDay: number; startOfDay: number } {
    const startOfDay = moment.tz(selectedDate, timezone).startOf('day');
    const nextDay = startOfDay.clone().add(1, 'day');

    return {
      startOfDay: startOfDay.valueOf(),
      endOfDay: nextDay.valueOf(),
    };
  }

  getPartyById(partyId: string): Party | undefined {
    return this.query.getEntity(partyId);
  }

  getAll(): Party[] {
    return this.query.getAll();
  }

  reset(): void {
    this._includeSeated = false;
    this.store.reset();
  }

  listenToChanges() {
    const sequenceCheck$ = combineLatest([
      this.socketService.onSequenceMetadata,
      this.locationConfig
    ]).pipe(
      tap(([{ event, sequenceNumber, date }, config]) => {
        if (config.locationId) {
          this.eventSequenceService.checkAndUpdate(
            config.locationId,
            sequenceNumber,
            date,
            event
          );
        }
      }),
      ignoreElements(),
    );

    const gapReload$ = this.eventSequenceService.onGapDetected.pipe(
      scan((acc, gap) => ({
        count: acc.count + 1,
      }), { count: 0 }),
      throttleTime(10000, undefined, { leading: true, trailing: true }),
      tap((info) => {
        console.warn(`Reloading after ${info.count} gaps...`);
      }),
      exhaustMap(() =>
        this.load().pipe(
          catchError(() => {
            return EMPTY;
          })
        )
      ),
      tap(() => console.info('Reload completed')),
      ignoreElements(),
    );

    return merge(
      sequenceCheck$,
      gapReload$,
      this.socketService.getEventStream<Party[]>(WebSocketEventsEnum.partyCreated).pipe(
        filter((parties) => parties.length > 0),
        tap((parties) => this.upsertParties(parties)),
      ),
      this.socketService.getEventStream<Party[]>(WebSocketEventsEnum.partyUpdated).pipe(
        filter((parties) => parties.length > 0),
        tap((parties) => {
          for (let i = 0; i < parties.length; i++) {
            const party = parties[i];
            if (this.shouldBeInStore(party)) {
              this.upsertParty(party);
            } else {
              // remove() of an id the store doesn't hold emits nothing, so a change to an
              // already-completed party would otherwise never reach includeCompleted consumers.
              this.store.remove(party._id);
              this.nonStorePartyChange$.next();
            }
          }
        }),
      ),
      this.socketService
        .getEventStream<string[]>(WebSocketEventsEnum.partyDeleted)
        .pipe(tap(ids => {
          this.store.remove(ids);
          this.nonStorePartyChange$.next();
        }))
    );
  }

  private shouldBeInStore(party: Party): boolean {
    return party.canceled_time == null
      && party.type !== PartyTypes.togo
      && party.completedAt === 0;
  }

  private shouldBeInWaitlist(
    party: Party,
    config: {
      autoRemove: number;
      daysAhead: number;
      reservationWaitlistHoursAhead: number;
      hideNoPhone: boolean;
      showWaiting: boolean;
    }
  ): boolean {
    if (!this.shouldBeInStore(party)) {
      return false;
    }

    if (party.seated_time !== null) {
      return false;
    }

    const now = Date.now();
    const start = now - config.autoRemove * MS_PER_HOUR;
    const end = now + config.daysAhead * MS_PER_DAY;

    if (party.quoted_time <= start) {
      return false;
    }

    const isOutOfRange = party.type === PartyTypes.reservation
      ? !isInReservationWaitlistWindow(
        party.quoted_time,
        config.reservationWaitlistHoursAhead,
        now,
      )
      : party.quoted_time >= end;
    if (isOutOfRange) {
      return false;
    }

    // Exclude square parties without phone number
    return !(config.hideNoPhone && party.source === PartySource.square && party.phone === '');
  }

  private shouldBeInReservations(
    party: Party,
    config: {
      autoRemove: number;
    }
  ): boolean {
    if (!this.shouldBeInStore(party)) {
      return false;
    }

    if (party.type !== PartyTypes.reservation || party.seated_time !== null) {
      return false;
    }

    const now = Date.now();
    const autoRemoveMs = config.autoRemove * MS_PER_HOUR;

    return party.quoted_time + autoRemoveMs > now;
  }

  private sort(filteredParties: Party[], sort: string, sortReverse: SortDirection): void {
    if (sort === 'service' || sort === 'resource') {
      // Sort services/resources in parties by names for correct comparison in sorting function
      for (let i = 0; i < filteredParties.length; i++) {
        sortServicesResources(filteredParties[i][`${sort}s`], this.serviceIdMap);
      }

      filteredParties.sort(getSortByServicesResources(
        sortReverse,
        `${sort}s`,
        sort === 'service' ? this.serviceIdMap : this.resourceIdMap,
      ));

      return;
    }

    filteredParties.sort(getSortFunction(sort, sortReverse));
  }

  private getWaitingParties(parties: Party[]): {waitingParties: WaitingParty[]; guestCount: number;} | undefined {
    if (this.showWaiting) {
      const waitingParties = getWaitingTemplate();
      const guestCount = parties.reduce((acc, party) => {
        if (party.checkin_time && !party.canceled_time && !party.seated_time) {
          const headcount = normalizePartySize(party.size);
          waitingParties[getSizeGroup(headcount)].count++;
          acc += headcount;
        }
        return acc;
      }, 0);

      return {waitingParties, guestCount};
    }

    return undefined;
  }
}

enum PartySortProperties {
  name = 'name',
  size = 'size',
  quoted_time = 'quoted_time',
  checkin_time = 'checkin_time',
  note = 'note',
  wait_time = 'wait_time',
}

type SortDirection = 1 | -1;

function getSortFunction(property: string, direction: SortDirection) {
  const _direction = direction === -1 ? -1 : 1;

  switch (property) {
    case PartySortProperties.name:
    case PartySortProperties.size:
    case PartySortProperties.note:
      return getSortByProperty(property, _direction);
    case PartySortProperties.quoted_time:
    case PartySortProperties.checkin_time:
      return getSortNullableByTimestamp(property, _direction);
    case PartySortProperties.wait_time:
      // Sorting by check in time, but in reverse order
      return getSortByProperty(PartySortProperties.checkin_time, _direction === 1 ? -1 : 1);
    case 'close_time':
      return getSortByCloseTime(_direction);
  }
}

function getSortByProperty(property: PartySortProperties, direction: SortDirection) {
  return function sortByProperty(party1: Party, party2: Party) {
    if (party1[property] === party2[property]) {
      return sortByColumn(party1._id, party2._id, direction);
    }

    if (direction === 1) {
      return party1[property] > party2[property] ? 1 : -1;
    }

    return party1[property] > party2[property] ? -1 : 1;
  }
}

function getSortNullableByTimestamp(
  property: PartySortProperties.checkin_time | PartySortProperties.quoted_time,
  direction: SortDirection,
) {
  return function sortByNullableProperty(party1: Party, party2: Party) {
    const a = party1[property];
    const b = party2[property];

    if (a === b) {
      return sortByColumn(party1.name, party2.name, 1);
    }

    if (direction === 1) {
      if (a === null) {
        return 1;
      }
      if (b === null) {
        return -1;
      }

      return a > b ? 1 : -1;
    }

    if (a === null) {
      return -1;
    }

    if (b === null) {
      return 1;
    }

    return a > b ? -1 : 1;
  };
}

function getSortByCloseTime(direction: SortDirection) {
  return function sortByCloseTime(party1: Party, party2: Party) {
    const value1 = party1.seated_time || party1.canceled_time;
    const value2 = party2.seated_time || party2.canceled_time;
    if (value1 === value2) {
      return sortByColumn(party1._id, party2._id, direction);
    }

    if (direction === 1) {
      return value1 > value2 ? 1 : -1;
    }

    return value1 > value2 ? -1 : 1;
  }
}

function getSortByServicesResources(direction: SortDirection, property: string, map: Record<string, number>) {
  return function sortByServicesResources(party1: Party, party2: Party) {
    const property1: string[] = party1[property];
    const property2: string[] = party2[property];
    if (property1.length === 0 && property2.length === 0) {
      return sortByColumn(party1._id, party2._id, direction);
    }

    const minLength = Math.min(property1.length, property2.length);
    for (let i = 0; i < minLength; i++) {
      const name1 = map[property1[i]];
      const name2 = map[property2[i]];
      if (name1 === name2) {
        return sortByColumn(party1._id, party2._id, direction);
      }
      if (direction === 1) {
        return name1 > name2 ? 1 : -1;
      }

      return name1 > name2 ? -1 : 1;
    }

    if (direction === 1) {
      return property1.length - property2.length;
    }

    return property2.length - property1.length;
  }
}

function sortByColumn(param1: string, param2: string, direction: SortDirection) {
  if (param1 === param2) {
    return 0;
  }

  if (direction === 1) {
    return param1 > param2 ? 1 : -1;
  }

  return param2 > param1 ? -1 : 1;
}

function sortServicesResources(data: string[], map: Record<string, number>) {
  data.sort((id1, id2) => {
    const name1 = map[id1];
    const name2 = map[id2];
    if (name1 === name2) {
      // Always ascending
      return sortByColumn(id1, id2, 1);
    }

    return name1 > name2 ? 1 : -1;
  });
}

interface Filters {
  search: string;
  sort: string;
  sortReverse: SortDirection;
  sizeGroup?: [number, number];
}
